import React, { useEffect, useState, useCallback } from 'react';
import { Lightbulb, RefreshCw } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { notificationsApi } from '../api/services';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Tooltip, TooltipContent, TooltipProvider, TooltipTrigger,
} from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';

// Coaching insights card. Backend already computes alerts across stalled drivers,
// overdue items, carry-forward tails, and red projects — this surface promotes
// them onto Insights → Reflections' close-day flow and the dashboard.

interface CoachingInsight {
  title: string;
  message: string;
  entityType?: string;
  entityId?: string;
}

interface CoachingInsightsCardProps {
  forExecutiveUserId?: string;
  title?: string;
  clickable?: boolean;
}

function insightHref(entityType?: string, entityId?: string): string | null {
  if (!entityType || !entityId) return null;
  switch (entityType) {
    case 'Project': return `/projects/${entityId}`;
    // Drivers and KPIs live on /drivers.
    case 'StrategicDriver': return '/drivers';
    case 'WorkItem': return `/work?id=${entityId}`;
    case 'Decision': return `/decisions?id=${entityId}`;
    case 'Kpi':
    case 'StrategicKpi': return '/drivers';
    default: return null;
  }
}

export default function CoachingInsightsCard({
  forExecutiveUserId,
  title = 'Coaching insights',
  clickable = true,
}: CoachingInsightsCardProps) {
  const navigate = useNavigate();
  const [insights, setInsights] = useState<CoachingInsight[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await notificationsApi.getCoachingInsights(forExecutiveUserId);
      setInsights(Array.isArray(result) ? result : []);
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Coaching insights unavailable.');
    } finally {
      setLoading(false);
    }
  }, [forExecutiveUserId]);

  useEffect(() => { load(); }, [load]);

  if (!loading && !error && (!insights || insights.length === 0)) {
    return null;
  }

  return (
    <Card className="mb-3 border-l-[3px] border-l-sky-500 bg-card">
      <CardContent className="py-3">
        <div className="mb-2 flex items-center gap-1">
          <Lightbulb className="h-5 w-5 text-sky-500" />
          <span className="text-sm font-semibold text-sky-500">{title}</span>
          <div className="flex-1" />
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7"
                  onClick={load}
                  disabled={loading}
                  aria-label="Refresh"
                >
                  <RefreshCw className={cn('h-4 w-4', loading && 'animate-spin')} />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Refresh insights</TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>

        {loading && (
          <p className="text-xs text-muted-foreground">Checking your week…</p>
        )}

        {error && (
          <Alert variant="warning" className="mt-1">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {!loading && !error && insights && (
          <div className="flex flex-col gap-2">
            {insights.slice(0, 5).map((ins, idx) => {
              const href = clickable ? insightHref(ins.entityType, ins.entityId) : null;
              const Wrapper: 'button' | 'div' = href ? 'button' : 'div';
              return (
                <Wrapper
                  key={idx}
                  type={href ? 'button' : undefined}
                  onClick={href ? () => navigate(href) : undefined}
                  className={cn(
                    'rounded-md bg-sky-500/[0.06] p-2 text-left',
                    href && 'cursor-pointer hover:bg-sky-500/[0.12]',
                  )}
                >
                  <p className="text-sm font-semibold">{ins.title}</p>
                  <p className="mt-0.5 text-sm text-muted-foreground">{ins.message}</p>
                </Wrapper>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
